import { getCargoNameValidationMessage, COUNTRY_CHECK_MESSAGE } from '../../../utils/cargoNameValidation';
import { resolveInvoiceCountries } from '../../../utils/resolveInvoiceCountries';
import { reportCargoNameValidation } from '../../../utils/cargoNameValidationDiagnostics';
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useInvoiceContext } from "../createInvoiceContext";
import "./resultpanel.css";
import BasicPanel from "../../../components/panels/basicPanel/basicPanel";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { Toggle, Button, Panel, Modal, Message, useToaster } from "rsuite";
import { useTranslation } from "react-i18next";

import InputVoiceSubmit from "../../../components/inputVoice/inputVoiceSubmit";
import ShareButton from "../../../components/shareButton/ShareButton";

import { createInvoice, callback, createTemplate, callback_template, updateTemplate } from "../../../api/logistic";
import arrangeInvoiceParams from "./utils/arrangeInvoiceParams";
import { formatInvoiceCreationFailure } from "../../../utils/invoiceCreationError";
import { useMessenger } from "../../../context/messengerContext";
import { buildUserIdPayload } from "../../../utils/userIdPayload";
import BasicCheckbox from "../../../components/basicCheckbox/basicCheckbox";
import { getCurrencySymbol } from "../../../utils/currencyHelper";
import { checkEAEUFieldsChanged, shouldApplyEAEULogic } from "../../../utils/eaeuHelper";
import EAEUManagerModal from "../../../components/eaeuManagerModal/eaeuManagerModal";
import { useLanguage } from "../../../context/localeContext";
import Btn from "../../../components/Btn/Btn";
import { useNavigate } from "react-router-dom";

const CARET_SVG =
    <svg width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.298162 8.13654C-0.0659236 7.78507 -0.0659236 7.21522 0.298163 6.86375L6.51351 0.863751C6.8776 0.512278 7.4679 0.512278 7.83198 0.863751L13.702 6.53042C14.0661 6.88189 14.0661 7.45174 13.702 7.80321C13.3379 8.15468 12.7476 8.15468 12.3836 7.80321L7.17275 2.77294L1.61664 8.13654C1.25255 8.48801 0.66225 8.48801 0.298162 8.13654Z" fill="#17C653"/>
    </svg>

const ARROW_RIGHT = 
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.60039 7.19995L14.4004 12L9.60039 16.8" stroke="#6F6F6F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>


const getDeliveryTypesFrom = (t) => ({
  1: t("door"),
  2: t("door"),
  3: t("warehouse"),
  4: t("warehouse"),
})

const getDeliveryTypesTo = (t) => ({
  1: t("door"),
  2: t("warehouse"),
  3: t("door"),
  4: t("warehouse"),
})

function capitalizeFirstLetter(val) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

const LabelWithSubLabel = ({ label, subLabel }) => {
  return (
      <div className={`labelWithSubLabel`}>
          <div className="paragraph-14R" style={{color: "var(--Gray-800)"}}>{label}</div>
          <div className="paragraph-12R" style={{color: "var(--Gray-600) !important"}}>{subLabel}</div>
      </div>
  )
}

const ValueWithSubValue = ({ value, subValue }) => {

  const cleanedValue = value?.trim().replace(/,$/, '');  // Удаляем запятую в конце, если есть
  const cleanedSubValue = subValue?.trim().replace(/,$/, '');  // То же для subValue
  return (
      <div className={`valueWithSubValue`}>
          <div className="paragraph-14R" style={{color: "var(--Gray-900)"}}>{cleanedValue}</div>
          <div className="paragraph-10R" style={{color: "var(--Gray-600)"}}>{cleanedSubValue}</div>
      </div>
  )
}

const formatValue = (val, currencySymbol = '₽') => {
  if (typeof val === "string" && (val.includes("Р") || val.includes("₽") || val.includes("₸"))) {
    const num = parseFloat(val.replace(/[^\d.]/g, ""));
    if (!isNaN(num)) {
      return Math.ceil(num) + " " + currencySymbol;
    }
  }
  return val;
};

const LabelWithValueAndKey = ({ label, subLabel, value, subValue, noTranslate }) => {
  const { t } = useTranslation('basic');
  const { invoiceData } = useInvoiceContext();
  const currencySymbol = getCurrencySymbol(invoiceData?.user_info);

  return (
      <div className={`labelWithValueAndKey`}>
          <LabelWithSubLabel label={noTranslate ? label : t(label)} subLabel={subLabel} />
          <ValueWithSubValue value={formatValue(value, currencySymbol)} subValue={subValue} />
      </div>
  )
}

const DisplayPanel = ({ title, children }) => {
  return (
    <BasicPanel defaultExpanded caretAs={() => <span className="panelCaret">{CARET_SVG}</span>} collapsible header={title}>{children}</BasicPanel>
  );
}

export function getInvoiceCreationErrorMessage(response, fallbackMessage) {
  const restrictionMessage = typeof response?.restriction_message === "string"
    ? response.restriction_message.trim()
    : "";

  if (restrictionMessage) return restrictionMessage;

  const restrictionErrors = (response?.requests || [])
    .flatMap(request => request.errors || [])
    .map(error => error.message || error.code)
    .filter(Boolean);

  return restrictionErrors.join(" ") || fallbackMessage;
}

export default function ResultPanel({startTime, isTemplate, isTemplateUpdate}) {
  const { t } = useTranslation('basic');
  const toaster = useToaster();
  const { invoiceData, setInvoiceData, clearInvoiceCache } = useInvoiceContext();
  const currencySymbol = getCurrencySymbol(invoiceData?.user_info);

  const [preparedDataMain, setPreparedDataMain] = useState({});
  const [prepatedDataAdditions, setPrepatedDataAdditions] = useState({});
  const [preparedDataSubject, setPreparedDataSubject] = useState({});
  const [preparedDataPlaces, setPreparedDataPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(0);

  const [needBarcode, setNeedBarcode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [invoiceUuid, setInvoiceUuid] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isKzContract, setIsKzContract] = useState(false);

  const [shareInvoiceData, setShareInvoiceData] = useState(null);
  const shareInvoiceRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const { userId, platform, close, goToMenu } = useMessenger();
  const {  language } = useLanguage();
  const navigate = useNavigate();

  const splideRef = useRef();

  /** @param {object | undefined} response */
  const showCreationError = (response) => {
    toaster.push(
      <Message type="error" showIcon role="alert" style={{ whiteSpace: 'pre-line' }}>
        {formatInvoiceCreationFailure(response, t)}
      </Message>,
      { placement: 'topCenter', duration: 3000 }
    );
  };

  console.log('invoiceData', invoiceData)

  useEffect(() => {
    // Сохраняем данные для шаринга в момент успешного создания накладной,
    // используя снапшот данных в shareInvoiceRef, чтобы избежать зависимостей от invoiceData после очистки кэша
    if (!success || !shareInvoiceRef.current) return;
    const snapshot = shareInvoiceRef.current;
    const is_kz = snapshot?.contract_number?.toUpperCase().includes("KZ");
    setIsKzContract(is_kz);
    setShareInvoiceData({
      title: t("invoice_created"),
      text: [
        `Накладная: ${snapshot.uuid}`,
        `Маршрут: ${snapshot.cityFromLabel} → ${snapshot.cityToLabel}`,
        `Тариф: ${snapshot.tariff_name}`,
        `Срок: ${snapshot.period_min}–${snapshot.period_max} раб. дней`,
        `Стоимость по договору: ${snapshot.contract_pay} ${snapshot.currencySymbol}`,
      ].join("\n"),
    });
  }, [success, t]);

  useEffect(() => {
      if (!splideRef.current) return;

      splideRef.current.go(selectedPlace, {allowSameIndex: true});
      console.log(splideRef.current);
  }, [splideRef, selectedPlace]);

  useEffect(() => {
    if (success) setShowSuccessModal(true);
  }, [success]);

  // Prepare data main
  useEffect(() => {
    let height = invoiceData.places.reduce((acc, place) => acc + place.height, 0);
    let width = invoiceData.places.reduce((acc, place) => acc + place.width, 0);
    let length = invoiceData.places.reduce((acc, place) => acc + place.length, 0);

    let total_size = `${height} x ${length} x ${width} ${t('cm_short')}`;
    let total_weight = invoiceData.places.reduce((acc, place) => acc + place.weight, 0);
    let total_calculated_weight = invoiceData.places
      .reduce((acc, place) => acc + (place.height * place.width * place.length) / 5000, 0)
      .toFixed(2);

    total_calculated_weight = parseFloat(total_calculated_weight).toString();

    let main = {
      "total_size": {
        "subLabel": "",
        "value": (total_size || 0),
        "subValue": ""
      },
      "total_weight": {
        "subLabel": "",
        "value": (total_weight || 0) + " " + t('kg_short'),
        "subValue": ""
      },
      "calculate_weight": {
        "subLabel": "",
        "value": (total_calculated_weight || 0) + " " + t('kg_short'),
        "subValue": ""
      },
      "cityFrom": {
        "subLabel": "",
        "value": invoiceData.cityFromLabel,
        "subValue": ""
      },
      "cityTo": {
        "subLabel": "",
        "value": invoiceData.cityToLabel,
        "subValue": ""
      },
      "deliveryType": {
        "subLabel": "",
        "value": getDeliveryTypesFrom(t)[invoiceData.selected_mode] + "-" + getDeliveryTypesTo(t)[invoiceData.selected_mode],
        "subValue": ""
      },
      "deliveryMode": {
        "subLabel": "",
        "value": invoiceData.tariff_name?.split(" ")?.[0],
        "subValue": ""
      },
      "deliveryPeriod": {
        "subLabel": "",
        "value": invoiceData.period_min && invoiceData.period_max 
          ? `${t("from")} ${invoiceData.period_min} ${t("to")} ${invoiceData.period_max} ${t("working_days")}`
          : t("not_specified"),
        "subValue": ""
      }
    }

    let additions = {}

    Object.keys(invoiceData.additions).map((key, i) => {
      let subValue;

      if (invoiceData.additions_types[key] == "bool") {
        subValue = "";
      }
      if (invoiceData.additions_types[key] == "int") {
        subValue = invoiceData.additions[key] + " " + t("pcs");
      }

      additions[key] = {
        subLabel: "",
        subValue: subValue,
        value: invoiceData.additions_separated_prices[key],
      }
    });

    setPreparedDataMain({
      ...main,
    });
  }, [])

  useEffect(() => {
    let additions = {}

    Object.keys(invoiceData.additions).map((key, i) => {
      if (key.startsWith("GET_UP")) {
        if (key.startsWith("GET_UP_")) return;

        additions[key] = {
          subValue: `${invoiceData.additions.GET_UP} ${t("floor_word")}` + (!invoiceData.additions.GET_UP_FLOOR_BY_HAND ? `, ${t("without_elevator")}` : `, ${t("with_elevator")}`),
          value: invoiceData.additions_separated_prices.GET_UP ? invoiceData.additions_separated_prices.GET_UP + " " + currencySymbol : t("free"),
        }

        return;
      }

      if (key.startsWith("BOX")) return;

      let subValue;

      if (invoiceData.additions_types[key] == "bool") {
        subValue = "";
      }
      if (invoiceData.additions_types[key] == "int") {
        subValue = invoiceData.additions[key] + " " + t("pcs");
      }

      additions[key] = {
        subLabel: "",
        subValue: subValue,
        value: invoiceData.additions_separated_prices[key] ? invoiceData.additions_separated_prices[key] + " " + currencySymbol : key == "LATHING" ? t("manager_will_inform_price") : t("free"),
      }
    });

    setPrepatedDataAdditions({
      ...additions,
    });
  }, [])

  useEffect(() => {
    let subject = {
      sender: {},
      recipient: {}
    }
    let subjects = ["sender", "recipient"];

    console.log("ASDASD")

    subjects.map((key, i) => {
      console.log(key);
      Object.keys(invoiceData.delivery[key]).map((key_, i) => {
        if (key_ == "phones") {
          subject[key].number = {
            subLabel: "",
            subValue: "",
            value: invoiceData.delivery[key][key_][0].number,
          }

          if (invoiceData.delivery[key][key_][0].additional) {
            subject[key].additional = {
              subLabel: "",
              subValue: "",
              value: invoiceData.delivery[key][key_][0].additional,
            }
          }

          return;
        }
        subject[key][key_] = {
          subLabel: "",
          subValue: "",
          value: invoiceData.delivery[key][key_],
        }
      });
    })

    if (invoiceData.addressFrom && [1, 2].includes(invoiceData.selected_mode)) {
      subject.sender.address = {
        subLabel: "",
        subValue: "",
        value: invoiceData.addressFrom,
      }
    }

    if (invoiceData.addressTo && [1, 3].includes(invoiceData.selected_mode)) {
      subject.recipient.address = {
        subLabel: "",
        subValue: "",
        value: invoiceData.addressTo,
      }
    }

    if (invoiceData.pvzTo && [2, 4].includes(invoiceData.selected_mode)) {
      subject.recipient.pvz = {
        subLabel: "",
        subValue: "",
        value: invoiceData.pvzToLabel,
      }
    }

    setPreparedDataSubject({
      ...subject,
    });
  }, [])

  useEffect(() => {
    let places = [];

    invoiceData.places.map((place, i) => {
      let place_ = [];

      place.goods.map((good, i) => {
        place_.push({
          name: {
            subLabel: `${good.count} ${t("pcs_short")}` + (good.article ? `, ${t("art_short")} ${good.article}` : ""),
            label: <span className="paragraph-14M">{good.name}</span>,
            value: "",
            subValue: "",
            noTranslate: true
          },
          insurance: {
            subLabel: "",
            subValue: "",
            value: good.insurance + " " + currencySymbol,
          },
          weight: {
            subLabel: "",
            subValue: "",
            value: (good.weight || 0) + " " + t("kg_short"),
          },
          vat: {
            subLabel: "",
            subValue: "",
            value: good.vat < 1 ? t("without_vat") : ( (good.vat <= 1 ? 0 : good.vat) || 0 ) + " %",
          },
          vat_sum: {
            subLabel: "",
            subValue: "",
            value: (good.vat_sum || 0) + " " + currencySymbol,
          },
          price: {
            subLabel: "",
            subValue: "",
            value: good.price + " " + currencySymbol,
          }
        })
      })

      places.push(place_);
    });

    console.log(places);
    setPreparedDataPlaces(places)
  }, []);

  return (
    <div className="resultPanel">
        <DisplayPanel title={t("calculation_params")}>
          <div className="resultPanelRows">
            {
              Object.keys(preparedDataMain).map((key, i) => {
                return <LabelWithValueAndKey key={i} label={key} {...preparedDataMain[key]} />;
              })
            }
          </div>
        </DisplayPanel>

        <DisplayPanel title={t("additional_services")}>
          <div className="resultPanelRows">
            { 
              Object.keys(prepatedDataAdditions).length ?
              Object.keys(prepatedDataAdditions).map((key, i) => {
                return <LabelWithValueAndKey key={i} label={key} {...prepatedDataAdditions[key]} />;
              }) : <div>{t("no_additional_services")}</div>
            }
          </div>
        </DisplayPanel>

        <DisplayPanel title={t("sender_info")}>
          <div className="resultPanelRows">
            { 
              preparedDataSubject.sender ?
              Object.keys(preparedDataSubject.sender).map((key, i) => {
                return <LabelWithValueAndKey key={i} label={key} {...preparedDataSubject.sender[key]} />;
              })
              : <div>{t("no_information")}</div>
            }
          </div>
        </DisplayPanel>

        <DisplayPanel title={t("receiver_info")}>
          <div className="resultPanelRows">
            { 
              preparedDataSubject.recipient ?
              Object.keys(preparedDataSubject.recipient).map((key, i) => {
                return <LabelWithValueAndKey key={i} label={key} {...preparedDataSubject.recipient[key]} />;
              })
              : <div>{t("no_information")}</div>
            }
          </div>
        </DisplayPanel>

        <BasicPanel className={"placeSelectorPanel"} header={
          <>
          <div className="arrowIcon arrowLeft"
            onClick={() => {
              setSelectedPlace(Math.max(selectedPlace - 1, 0));
            }}>
            {ARROW_RIGHT}
          </div>
          <Splide
            options={{
              height: "2rem",
              width: "70%",
              focus: "start",
              autoWidth: true,
              autoHeight: true,
              arrows: false,
              gap: preparedDataPlaces.length > 1 ? "1rem" : "0rem",
              index: 2
            }}

            ref={splideRef}
          > 
            {
              preparedDataPlaces.length ?
                preparedDataPlaces.map((place, i) => {
                  return (
                    <SplideSlide key={i} className={"placeSelectorWrapper"}>
                      <div className={`paragraph-14M placeSelector ${selectedPlace === i ? "selected" : ""}`} onClick={() => {
                        console.log(i);
                        setSelectedPlace(i);
                      }}>
                        {t("place_word")} {i + 1}
                      </div>
                    </SplideSlide>
                  )
                })
                : <div>{t("no_information")}</div>
            }
          </Splide>
          <div className="arrowIcon arrowRight"
          onClick={() => {
              setSelectedPlace(Math.min(selectedPlace + 1, preparedDataPlaces.length - 1));
            }}>
              {ARROW_RIGHT}
            </div>
          </>
        }>
            { 
              preparedDataPlaces.length && preparedDataPlaces[selectedPlace].map((good, i) => {
                console.log(good)
                return (
                  <div className="resultPanelRows good">
                    {
                     Object.keys(good).map((key, i) => {
                      console.log(good[key])
                        if (key === "price" && (!invoiceData.payment_on_receipt || good[key].value == " " + currencySymbol || good[key].value == "0 " + currencySymbol) || good[key].value == "null " + currencySymbol) return;
                        return <LabelWithValueAndKey key={i} noTranslate={key.noTranslate} label={key.label ? key.label : key} {...good[key]} />;
                     }) 
                    }
                  </div>
                )
              })
            }
        </BasicPanel>

        <div className="need-barcode-checkbox">
        {
          !isTemplate && !isTemplateUpdate &&
          <BasicCheckbox value={invoiceData.need_barcode} label={t("need_barcode")} onChange={(checked) => setInvoiceData(oldData => ({...oldData, need_barcode: checked}))} checked={invoiceData.need_barcode}/>
        }
        </div>

        <Panel className="resultFinalPanel">
            <div className="resultPanelRows">
              <LabelWithValueAndKey label={t("additional_services_cost")} noTranslate value={Math.ceil(invoiceData.additions_sum || 0) + " " + currencySymbol} />
              <LabelWithValueAndKey label={t("delivery_sum")} noTranslate value={Math.ceil(invoiceData.delivery_sum || 0) + " " + currencySymbol} />

              <div className={`labelWithValueAndKey`}>
                <div className={`labelWithSubLabel`}>
                  <div className="paragraph-14M" style={{color: "var(--Gray-900)"}}>{t("total_from_client")}</div>
                </div>
                <div className={`valueWithSubValue`}>
                    <div className="paragraph-14M" style={{color: "var(--Gray-900)"}}>{(invoiceData.delivery_pay_amount || 0) + " " + currencySymbol}</div>
                </div>
              </div>

              <div className={`labelWithValueAndKey`}>
                <div className={`labelWithSubLabel`}>
                  <div className="paragraph-14M" style={{color: "var(--Gray-900)"}}>{t("total_by_contract")}</div>
                </div>
                <div className={`valueWithSubValue`}>
                    <div className="paragraph-14M" style={{color: "var(--Gray-900)"}}>{(invoiceData.contract_pay || 0) + " " + currencySymbol}</div>
                </div>
              </div>
            </div>
        </Panel>

        {success ? (
          <div className="invoiceSuccessBlock">
            <div className="paragraph-14M" style={{ color: "var(--Gray-900)" }}>
              {t("invoice_created")}
            </div>
            <div className="paragraph-12R invoiceSuccessBlockUuid">{invoiceUuid}</div>
          </div>
        ) : (
          <InputVoiceSubmit loading={loading} onNavigateForward={async () => {
          if (loading) return;
          let submissionData = invoiceData;
          if (!isTemplate && !isTemplateUpdate) {
              setLoading(true);
              try {
                  submissionData = {...invoiceData, ...await resolveInvoiceCountries(invoiceData)};
              } catch (error) {
                  if (!mountedRef.current) return;
                  reportCargoNameValidation({event: 'cargo_name_submit_country_error', data: invoiceData,
                      message: COUNTRY_CHECK_MESSAGE, platform, userId});
                  toaster.push(<Message type="error" showIcon role="alert">{COUNTRY_CHECK_MESSAGE}</Message>,
                      { placement: "topCenter", duration: 3000 });
                  setLoading(false);
                  return;
              }
              if (!mountedRef.current) return;
              setInvoiceData(current => ({
                  ...current,
                  cityFromCountryCode: submissionData.cityFromCountryCode,
                  cityToCountryCode: submissionData.cityToCountryCode,
              }));
              const cargoMessage = getCargoNameValidationMessage(submissionData);
              reportCargoNameValidation({event: 'cargo_name_submit', data: submissionData,
                  message: cargoMessage, platform, userId});
              if (cargoMessage) {
                  toaster.push(<Message type="error" showIcon role="alert">{cargoMessage}</Message>,
                      { placement: "topCenter", duration: 3000 });
                  setLoading(false);
                  return;
              }
          }

          let d = arrangeInvoiceParams(submissionData);
          if (!isTemplate && !isTemplateUpdate) {
              d.source_country_code = submissionData.source_country_code;
              d.destination_country_code = submissionData.destination_country_code;
          }
          window.ym(100750465, "reachGoal", "clickButton-invoice");
          window.ym(100750465, "reachGoal", "clickButton-invoice-time", {
            "sesstion_time": new Date().getTime() - startTime.getTime()
          });

          if (isTemplateUpdate) {
            delete d.contract_number;
            try {
              await updateTemplate({
                template_id: invoiceData.template_id,
                params: {...d, created_by_manager: false}
              });
              navigate("/template_list");
            } catch (e) {
              console.error("updateTemplate failed", e);
            }

            return;
          }

          if (isTemplate) {
            let response = await createTemplate({
              contract_number: invoiceData.contract_number,
              params: d
            });

            const dateStartSec = Math.floor(startTime.getTime() / 1000);
            const dateEndSec = Math.floor(Date.now() / 1000);
            const session = {
              contract_number: invoiceData.contract_number,
              total_time: dateEndSec - dateStartSec,
              date_start: dateStartSec,
              date_end: dateEndSec
            };

            callback_template(
              {
                platform,
                userId: userId || "0",
                contract_number: invoiceData.contract_number,
                template_name: d.template_name,
                need_barcode: needBarcode,
                session
              }
            ).then(() => {
              close();
            });

            return;
          }

          setLoading(true);

          // Check if this is an EAEU shipment and if EAEU fields were changed
          const isEAEUShipment = shouldApplyEAEULogic(submissionData.cityFromCountryCode, submissionData.cityToCountryCode);
          // Pass translation function to ensure correct comparison with translated default values
          const fieldsChanged = checkEAEUFieldsChanged(submissionData, t);
          d.isStrictChanged = fieldsChanged;
          // Override modified_from_default to reflect whether fields were actually changed
          // (arrangeInvoiceParams sets it incorrectly to just check if it's EAEU)
          d.modified_from_default = fieldsChanged;

          // Запрос не переотправляется (REFACTORING-48), поэтому сбой сети виден сразу и
          // разлочить кнопку с сообщением надо здесь: иначе спиннер останется навсегда, а
          // пользователь пойдет создавать накладную заново вручную.
          let response;
          try {
              response = await createInvoice({
                  contract_number: invoiceData.contract_number,
                  platform,
                  userId: userId || "0",
                  params: {...d, ...buildUserIdPayload(platform, userId || "0"), creation_method: "manual",language: language || "ru"}
              });
          } catch (e) {
              console.error("createInvoice failed", e);
              showCreationError({ error: 'invoice_creation_unknown' });
              setLoading(false);
              return;
          }

          if (!response?.entity?.uuid) {
              showCreationError(response);
              setLoading(false);
              return;
          }

          if (response) {
              let uuid = response.entity?.uuid;
              
              if (uuid) {
                // Сохраняем снапшот данных для шаринга до очистки кэша
                shareInvoiceRef.current = {
                  uuid,
                  cityFromLabel: invoiceData.cityFromLabel,
                  cityToLabel: invoiceData.cityToLabel,
                  tariff_name: invoiceData.tariff_name,
                  period_min: invoiceData.period_min,
                  period_max: invoiceData.period_max,
                  contract_pay: invoiceData.contract_pay,
                  currencySymbol,
                  contract_number: invoiceData.contract_number,
                };

                clearInvoiceCache?.();
                setInvoiceUuid(uuid);
                
                // If fields were changed, close webapp immediately
                if (fieldsChanged) {
                  if (invoiceData.delivery_time_from) {
                    if (invoiceData.break_time_from) {
                      const dateStartSec = Math.floor(startTime.getTime() / 1000);
                      const dateEndSec = Math.floor(Date.now() / 1000);
                      const session = {
                        contract_number: invoiceData.contract_number,
                        total_time: dateEndSec - dateStartSec,
                        date_start: dateStartSec,
                        date_end: dateEndSec
                      };
                      callback({platform, userId: userId || "0",
                        contract_number: invoiceData.contract_number,
                        uuid,
                        couirer: {
                          intake_date: invoiceData.delivery_date_from,
                          intake_time_from: invoiceData.delivery_time_from,
                          intake_time_to: invoiceData.delivery_time_to,
                          lunch_time_from: invoiceData.break_time_from,
                          lunch_time_to: invoiceData.break_time_to
                        },
                        session: session}).then(() => {
                          setLoading(false);
                          setSuccess(true);
                        });
                    } else {
                      const dateStartSec = Math.floor(startTime.getTime() / 1000);
                      const dateEndSec = Math.floor(Date.now() / 1000);
                      const session = {
                        contract_number: invoiceData.contract_number,
                        total_time: dateEndSec - dateStartSec,
                        date_start: dateStartSec,
                        date_end: dateEndSec
                      };
                      callback({platform, userId: userId || "0",
                        contract_number: invoiceData.contract_number,
                        uuid,
                        couirer: {
                          intake_date: invoiceData.delivery_date_from,
                          intake_time_from: invoiceData.delivery_time_from,
                          intake_time_to: invoiceData.delivery_time_to
                        },
                        session: session}).then(() => {
                          setLoading(false);
                          setSuccess(true);
                        });
                    }
                  } else {
                    const dateStartSec = Math.floor(startTime.getTime() / 1000);
                    const dateEndSec = Math.floor(Date.now() / 1000);
                    const session = {
                      contract_number: invoiceData.contract_number,
                      total_time: dateEndSec - dateStartSec,
                      date_start: dateStartSec,
                      date_end: dateEndSec
                    };
                    callback({platform, userId: userId || "0",
                      contract_number: invoiceData.contract_number,
                      uuid,
                      session: session}).then(() => {
                        setLoading(false);
                        setSuccess(true);
                      });
                  }
                } else {
                  // Fields were not changed
                  // Show manager modal only for EAEU shipments
                  if (isEAEUShipment) {
                    setLoading(false);
                    setShowManagerModal(false);
                    setSuccess(true);
                  } else {
                    // For non-EAEU shipments (e.g. RF-RF), show success block
                    if (invoiceData.delivery_time_from) {
                      if (invoiceData.break_time_from) {
                        const dateStartSec = Math.floor(startTime.getTime() / 1000);
                        const dateEndSec = Math.floor(Date.now() / 1000);
                        const session = {
                          contract_number: invoiceData.contract_number,
                          total_time: dateEndSec - dateStartSec,
                          date_start: dateStartSec,
                          date_end: dateEndSec
                        };
                        callback({platform, userId: userId || "0",
                          contract_number: invoiceData.contract_number,
                          uuid,
                          couirer: {
                            intake_date: invoiceData.delivery_date_from,
                            intake_time_from: invoiceData.delivery_time_from,
                            intake_time_to: invoiceData.delivery_time_to,
                            lunch_time_from: invoiceData.break_time_from,
                            lunch_time_to: invoiceData.break_time_to
                          },
                          session: session}).then(() => {
                            setLoading(false);
                            setSuccess(true);
                          });
                      } else {
                        const dateStartSec = Math.floor(startTime.getTime() / 1000);
                        const dateEndSec = Math.floor(Date.now() / 1000);
                        const session = {
                          contract_number: invoiceData.contract_number,
                          total_time: dateEndSec - dateStartSec,
                          date_start: dateStartSec,
                          date_end: dateEndSec
                        };
                        callback({platform, userId: userId || "0",
                          contract_number: invoiceData.contract_number,
                          uuid,
                          couirer: {
                            intake_date: invoiceData.delivery_date_from,
                            intake_time_from: invoiceData.delivery_time_from,
                            intake_time_to: invoiceData.delivery_time_to
                          },
                          session: session}).then(() => {
                            setLoading(false);
                            setSuccess(true);
                          });
                      }
                    } else {
                      const dateStartSec = Math.floor(startTime.getTime() / 1000);
                      const dateEndSec = Math.floor(Date.now() / 1000);
                      const session = {
                        contract_number: invoiceData.contract_number,
                        total_time: dateEndSec - dateStartSec,
                        date_start: dateStartSec,
                        date_end: dateEndSec
                      };
                      callback({platform, userId: userId || "0",
                        contract_number: invoiceData.contract_number,
                        uuid,
                        session: session}).then(() => {
                          setLoading(false);
                          setSuccess(true);
                        });
                    }
                  }
                }
              }

              return;
          }
          
          setLoading(false);
      }} />
        )}
      
      <EAEUManagerModal
        open={showManagerModal}
        onClose={() => setShowManagerModal(false)}
        onSkip={() => {
          setShowManagerModal(false);
        }}
        contractNumber={invoiceData.contract_number}
      />

      {success && (
        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          size="xs"
        >
          <Modal.Body>
            <div className="invoiceSuccessActions">
              <div className="invoiceSuccessBlock">
                <div className="paragraph-14M" style={{ color: "var(--Gray-900)" }}>{t("invoice_created")}</div>
                <div className="paragraph-12R invoiceSuccessBlockUuid">{invoiceUuid}</div>
                {isKzContract && (
                  <div className="paragraph-12R" style={{ color: "var(--Gray-700)" }}>
                    {t("eaeu_manager_modal_text")}
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className='invoiceSuccessFooter'>
                <ShareButton className="primary" data={shareInvoiceData}>
                  {t('share_button')}
                </ShareButton>
                {isKzContract && (
                  <Btn
                    text={t("chat_with_manager")}
                    fluid
                    onClick={() => {
                      const contractNumber = shareInvoiceRef.current?.contract_number || invoiceData.contract_number;
                      setShowSuccessModal(false);
                      navigate(`/enter_chat?contract_number=${contractNumber}`);
                    }}
                  />
                )}
                <Btn text={t("go_to_menu")} fluid onClick={() => goToMenu()} />
              </div>
            </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}
